import React, { useEffect, useState } from "react";
import { PageLoader } from "../../ui";
import VaptSeverityBadge from "../components/VaptSeverityBadge";
import VaptStatusBadge from "../components/VaptStatusBadge";
import { listVaptRemediationApi, listVaptFindingsApi } from "../../../services/axios/AuthApis/VAPTApis";
import { VAPT_REMEDIATION_STATUSES, VAPT_SEVERITIES } from "../utils/vaptConstants";
import { DataTable } from "../../common/DataTable";

const asArray = (data) => (Array.isArray(data) ? data : data?.results || []);

const VaptRemediation = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    Promise.all([listVaptRemediationApi(), listVaptFindingsApi()])
      .then(([remRes, findingsRes]) => {
        const findingsById = {};
        asArray(findingsRes).forEach((f) => {
          findingsById[f.finding_id] = f;
        });
        const rows = asArray(remRes).map((r, i) => {
          const finding = findingsById[r.finding] || {};
          return {
            // r.finding is the internal Mongo ObjectId of the referenced
            // VAPTFinding — safe to use as a non-visible React key fallback,
            // never as displayed text (see title/project below).
            id: r.remediation_id ?? `remediation-${i}`,
            title: finding.title || r.finding_title || "Untitled Finding",
            severity: finding.severity,
            status: r.status,
            owner: r.owner || r.assigned_to || "—",
            dueDate: r.due_date || "—",
            project: finding.project_name || finding.project_display_id || "Asset VAPT Project",
          };
        });
        setItems(rows);
        setLoading(false);
      })
      .catch((err) => {
        setError(err?.message || "Failed to load remediation tracker.");
        setLoading(false);
      });
  }, []);

  if (loading) return <PageLoader title="Loading remediation tracker…" />;
  if (error) {
    return (
      <div className="text-center py-16 bg-white rounded-xl border border-gray-200">
        <p className="text-gray-500">{error}</p>
      </div>
    );
  }

  const uniqueOwners = [...new Set(items.map((i) => i.owner).filter((v) => v && v !== "—"))];
  const uniqueProjects = [...new Set(items.map((i) => i.project).filter(Boolean))];

  const columns = [
    {
      id: "title",
      accessorFn: (r) => r.title,
      header: "Finding",
      enableSorting: false,
      meta: { filter: { variant: "text", label: "Finding", placeholder: "Search finding" } },
      cell: ({ row }) => <span>{row.original.title}</span>,
    },
    {
      id: "severity",
      accessorFn: (r) => r.severity,
      header: "Severity",
      enableSorting: false,
      meta: {
        filter: {
          variant: "select",
          label: "Severity",
          placeholder: "All severities",
          options: VAPT_SEVERITIES.map((v) => ({ value: v, label: v })),
        },
      },
      cell: ({ row }) => <VaptSeverityBadge severity={row.original.severity} size="sm" />,
    },
    {
      id: "status",
      accessorFn: (r) => r.status,
      header: "Status",
      enableSorting: false,
      meta: {
        filter: {
          variant: "select",
          label: "Status",
          placeholder: "All statuses",
          options: VAPT_REMEDIATION_STATUSES.map((v) => ({ value: v, label: v })),
        },
      },
      cell: ({ row }) => <VaptStatusBadge status={row.original.status} size="sm" />,
    },
    {
      id: "owner",
      accessorFn: (r) => r.owner,
      header: "Owner",
      enableSorting: false,
      meta: {
        filter: {
          variant: "select",
          label: "Owner",
          placeholder: "All owners",
          options: uniqueOwners.map((v) => ({ value: v, label: v })),
        },
      },
      cell: ({ row }) => <span>{row.original.owner}</span>,
    },
    {
      id: "dueDate",
      accessorFn: (r) => r.dueDate,
      header: "Due Date",
      enableSorting: false,
      cell: ({ row }) => <span>{row.original.dueDate}</span>,
    },
    {
      id: "project",
      accessorFn: (r) => r.project,
      header: "Project",
      enableSorting: false,
      meta: {
        filter: {
          variant: "select",
          label: "Project",
          placeholder: "All projects",
          options: uniqueProjects.map((v) => ({ value: v, label: v })),
        },
      },
      cell: ({ row }) => <span>{row.original.project}</span>,
    },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-bold text-gray-900">Remediation Tracker</h2>
        <p className="text-sm text-gray-500">{items.length} items</p>
      </div>

      <DataTable
        columns={columns}
        data={items}
        getRowId={(r) => String(r.id)}
        emptyMessage="No remediation records yet."
        enableColumnVisibility={false}
        enableColumnResizing
        enableColumnFilters
        maxBodyHeight="65vh"
      />
    </div>
  );
};

export default VaptRemediation;
